/**
 * Engine-hosted sales pages for the TSB membership tiers — Alumni,
 * Member Pro (staged behind the E27 gate, see lib/launchGates.ts) and
 * The Boardroom.
 *
 * Rendered by ProgramSalesPage (route /programs/:slug) when the slug has an
 * entry in MEMBERSHIP_CONTENT. The parent has already fetched
 * /api/public/programs/:slug, applied the program palette + fonts to the DOM
 * (--prog-* / --font-* custom properties), loaded Google Fonts, and set the
 * document title — this component only renders the page.
 *
 * Alumni and Boardroom are membership tiers (tiers v2, locked 2026-04-19),
 * not cohort programs — they ride the programs machinery because it serves
 * the same public surfaces. Copy is ported verbatim from the locked www
 * HubSpot pages (site-pages 211266180147 / 211266257317, retired 2026-06-11).
 *
 * CTAs:
 *  - Alumni: interest form (waitlist, source discover-alumni-interest) until
 *    the $275/mo Spiffy checkout exists; when the active cohort gains
 *    commerce config the pricing CTA switches to /programs/alumni/order.
 *  - Boardroom: application-only by design — the form posts to the waitlist
 *    (source discover-boardroom-application); the team follows up
 *    (express interest → conversation → invitation).
 */

import { useState } from 'react';
import type { ProgramPublicResponse } from './ProgramSalesPage';
import { PolicyLinks } from './components/PolicyLinks';
import { useNoIndex } from './lib/useNoIndex';
import { STAGED_SLUGS } from './lib/launchGates';

const API = import.meta.env.VITE_API_BASE_URL as string;

// ────────────────────────────────────────────────────────────────────────────
// Content
// ────────────────────────────────────────────────────────────────────────────

interface TierContent {
  /** Short tier name for inline links ("Talk with our team about …"). */
  name: string;
  theme: 'light' | 'dark';
  eyebrow: string;
  h1Plain: string;
  /** Optional un-accented lead-in on the second h1 line (e.g. "at "). */
  h1AccentPrefix?: string;
  h1Accent: string;
  heroSub: string;
  heroMeta: string;
  heroCtaLabel: string;
  introH2Plain: string;
  introH2Accent: string;
  introBody: string;
  /** Top card row — Alumni pillars / Boardroom format cards. */
  cards: Array<{ label?: string; title: string; body: string; bullets?: string[] }>;
  people?: {
    h2: string;
    sub: string;
    items: Array<{ initials: string; name: string; role: string; bio: string }>;
  };
  includedH2: string;
  included: Array<{ title: string; body: string }>;
  forNot?: {
    h2: string;
    forTitle: string;
    forItems: string[];
    notTitle: string;
    notItems: string[];
  };
  qualifiers?: {
    h2: string;
    intro: string;
    items: Array<{ title: string; body: string }>;
  };
  testimonials?: {
    h2: string;
    items: Array<{ quote: string; initials: string; name: string; role: string }>;
  };
  pricing?: {
    eyebrow: string;
    price: string;
    suffix: string;
    terms: string;
    features: string[];
    ctaLabel: string;
  };
  applySteps?: {
    h2: string;
    sub: string;
    steps: Array<{ title: string; body: string }>;
  };
  form: {
    anchor: string;
    heading: string;
    sub: string;
    submitLabel: string;
    successHead: string;
    successBody: string;
    source: string;
  };
  faqs?: Array<{ q: string; a: string }>;
  finalH2Plain: string;
  finalH2Accent: string;
  finalSub: string;
  finalCtaLabel: string;
}

const ALUMNI_CONTENT: TierContent = {
  name: 'Alumni',
  theme: 'light',
  eyebrow: 'For program graduates',
  h1Plain: "The room you've been",
  h1Accent: 'looking for',
  heroSub:
    "Alumni isn't a program. It's the room. For bookkeepers who've completed a TSB program and want to keep growing alongside people who understand the journey.",
  heroMeta: '$275/month · Cancel anytime',
  heroCtaLabel: 'Join Alumni',

  introH2Plain: "It's not a program.",
  introH2Accent: "It's the room.",
  introBody:
    "You've done the work. You've been through the program, made the changes, and started seeing results. Alumni is where you stay connected to the people and the content that made it possible — and keep building from here.",

  cards: [
    {
      title: 'Your peer cohort',
      body: "Fellow program graduates who've been through the same work, faced the same challenges, and understand what it takes to build a business that actually works.",
    },
    {
      title: 'Everything. Always.',
      body: 'Full access to every TSB program — Freedom Gateway, Clients, Pricing, Scale — plus new programs and satellite workshops as they launch.',
    },
    {
      title: 'Continued growth',
      body: "Special Lisa-led calls, mentorship, and a community that grows with you — whether you're refining what you've built or reaching for what's next.",
    },
  ],

  includedH2: "What's included",
  included: [
    {
      title: 'All program content',
      body: 'Full access to Freedom Gateway, Clients, Pricing, and Scale — every lesson, resource, and framework.',
    },
    {
      title: 'New programs included',
      body: 'Every new program and satellite workshop we launch is yours automatically — no extra cost, no upsell.',
    },
    {
      title: 'Special Lisa-led calls',
      body: "Reserved for Alumni members only — strategy, Q&A, and the kind of conversation you can't get anywhere else.",
    },
    {
      title: '~30–40% off future programs',
      body: 'Flat discount on any program you want to take again — or any new program that launches after you join.',
    },
    {
      title: 'Your peer cohort',
      body: "A room of bookkeepers who've done the work — people who understand the journey you're on and what it took to get here.",
    },
    {
      title: "Pause, don't lose",
      body: "Enrolling in a new live program? Alumni pauses automatically so you never pay for both. Pick back up when you're ready.",
    },
  ],

  forNot: {
    h2: 'Is Alumni right for you?',
    forTitle: 'Alumni is for you if…',
    forItems: [
      "You've completed at least one TSB program and want to stay in the room",
      "You value connection with bookkeepers who've been through the same programs and understand what you're building",
      'You want ongoing access to every program, resource, and new launch without buying each one separately',
      "You're still growing — whether that's refining your systems, taking another program, or figuring out your next chapter — and you want a sounding board",
    ],
    notTitle: 'Alumni is not for you if…',
    notItems: [
      "You haven't completed a TSB program yet — Alumni unlocks after your first program",
      "You're looking for live coaching — Alumni includes content access, not live program delivery",
      'You want a one-time purchase, not an ongoing membership',
    ],
  },

  testimonials: {
    h2: 'From the room',
    items: [
      {
        quote:
          "Alumni is the room I didn't know I needed until I was in it. The people in here understand what it takes to build a real firm — and that changes everything about how you show up.",
        initials: 'AB',
        name: '',
        role: 'TSB Alumni Member',
      },
      {
        quote:
          "I thought I was done after Freedom Gateway. Turns out the growth after the program — when you're in the room with people who've been through it too — that's where the real shift happens.",
        initials: 'CD',
        name: '',
        role: 'TSB Alumni Member',
      },
    ],
  },

  pricing: {
    eyebrow: 'Alumni Membership',
    price: '$275',
    suffix: '/month',
    terms: 'Unlocks after your first program · Cancel anytime',
    features: [
      'All program content — Freedom Gateway, Clients, Pricing, Scale',
      'New programs and workshops included at launch',
      'Special Lisa-led calls — Alumni only',
      '~30–40% discount on future programs',
      'Pauses during active program enrollment',
    ],
    ctaLabel: 'Join Alumni',
  },

  form: {
    anchor: 'join',
    heading: 'Ready to stay in the room?',
    sub: "Leave your name and we'll reach out to get you set up — or book a call and talk it through first.",
    submitLabel: 'Count me in →',
    successHead: "You're on the list.",
    successBody: "We'll reach out shortly to get you set up. Talk soon.",
    source: 'discover-alumni-interest',
  },

  faqs: [
    {
      q: 'When does Alumni unlock?',
      a: "Alumni becomes available after you complete your first TSB program — whether that's Freedom Gateway, Clients, Pricing, or Scale. If you're on a payment plan, Alumni unlocks 90 days after enrollment (when your program period completes).",
    },
    {
      q: 'Can I cancel anytime?',
      a: "Yes. Alumni is month-to-month with no commitment. Cancel anytime and your access continues through the end of your billing period. You can rejoin whenever you're ready.",
    },
    {
      q: "What's the difference between Alumni and a program?",
      a: 'Programs are 90-day guided experiences with live coaching from Lisa and her team. Alumni gives you ongoing access to all program content, new launches, special calls, and your peer community — but without the live coaching component. Think of programs as the intensive work; Alumni as the permanent home you come back to.',
    },
    {
      q: 'Do I keep access to content if I pause?',
      a: "When you enroll in a new live program, your Alumni membership pauses automatically — you won't be charged for both. During your program, you have full content access through the program itself. Alumni resumes when your program period completes.",
    },
    {
      q: 'Can I upgrade back into a live program from Alumni?',
      a: "Absolutely. Alumni members receive a ~30–40% discount on any future program enrollment. When you're ready for another 90-day intensive — whether that's Clients, Pricing, Scale, or a new program we've launched — you enroll at the discounted rate and your Alumni pauses until you're done.",
    },
  ],

  finalH2Plain: 'You did the work.',
  finalH2Accent: 'Stay in the room.',
  finalSub:
    "Alumni is the permanent home for bookkeepers who've completed a TSB program and want to keep growing alongside people who get it.",
  finalCtaLabel: 'Join Alumni — $275/month',
};

const BOARDROOM_CONTENT: TierContent = {
  name: 'The Boardroom',
  theme: 'dark',
  eyebrow: 'Application-based · By invitation',
  h1Plain: "You've earned a seat",
  h1AccentPrefix: 'at ',
  h1Accent: 'the table',
  heroSub:
    "The Boardroom is for bookkeeping firm owners running $250K+ businesses who've completed the work and want to build what comes next — alongside Lisa Campbell, the TSB leadership team, and a small group of peers at your level.",
  heroMeta: '$975/month · Not a fit for most. The right fit for some.',
  heroCtaLabel: 'Register your interest',

  introH2Plain: "This isn't a course.",
  introH2Accent: "It's the room.",
  introBody:
    'The Boardroom is a small, curated group of bookkeeping firm owners building $250K+ businesses. Half-day sessions every quarter. An annual off-site. Direct access to Lisa Campbell, Michael Palmer, and the people who\'ve shaped this industry. This is where the next decade of your business gets built.',

  cards: [
    {
      label: 'Every quarter',
      title: 'Half-day intensive',
      body: 'A focused half-day session hosted by Lisa Campbell — with segments featuring Michael Palmer, Ron Bester, and guest speakers from across the industry.',
      bullets: [
        'Hosted by Lisa Campbell',
        'Direct conversation with Michael Palmer',
        'Guest speakers and industry leaders',
        'Annual curriculum with workbooks',
      ],
    },
    {
      label: 'Once a year',
      title: '2-day off-site',
      body: "An in-person gathering in the Toronto area. One overnight. The kind of conversations and connections that don't happen on a screen.",
      bullets: [
        'Two days with the full leadership team',
        'Deep strategy and planning',
        'In-person connection with your cohort',
        'Included in your membership',
      ],
    },
  ],

  people: {
    h2: "Who's in the room",
    sub: "Direct access to the people who've shaped this industry — not as guest appearances, but as your working partners.",
    items: [
      {
        initials: 'LC',
        name: 'Lisa Campbell',
        role: 'CEO, TSB Global',
        bio: 'Hosts every session. Built The Marcam Group from overwhelmed solo bookkeeper to high six-figure firm. Your primary working partner in The Boardroom.',
      },
      {
        initials: 'MP',
        name: 'Michael Palmer',
        role: 'Co-Founder, TSB',
        bio: "Joins every quarter for a direct conversation — no script, no slides. The podcast host, author, and the person who built what you're standing on.",
      },
      {
        initials: 'TS',
        name: 'Teresa Slack',
        role: 'Mentor & Former Firm Owner',
        bio: 'Built and sold a seven-figure bookkeeping business. Living proof that the ceiling is real and reachable. Your peer in the room, not just a name on the page.',
      },
      {
        initials: 'RB',
        name: 'Ron Bester',
        role: 'VP Client Experience',
        bio: '30+ years in business. A dedicated segment every quarter where you can talk strategy, client ecosystems, and the decisions only firm owners face.',
      },
      {
        initials: 'PC',
        name: 'Peter Cook',
        role: 'Co-Founder & Chair',
        bio: 'Coaching architect. Co-author of The E-Myth Bookkeeper. 23 years building the methodology beneath the system. Guest speaker.',
      },
      {
        initials: 'DR',
        name: 'Debbie Roberts',
        role: 'Founding Legend',
        bio: 'Co-founded Pure Bookkeeping. The original story. The system that changed bookkeeping started with her. Guest speaker.',
      },
    ],
  },

  includedH2: 'Everything. Always.',
  included: [
    {
      title: 'Full program access',
      body: 'Every TSB program — Freedom Gateway, Clients, Pricing, Scale — plus anything we launch in the future. Yours automatically.',
    },
    {
      title: 'Exclusive trainings',
      body: 'Content created only for Boardroom members. Topics at the frontier — AI, advisory services, firm architecture, and what comes next.',
    },
    {
      title: 'Any call, any time',
      body: 'Access to every live call across the TSB ecosystem. Drop into any session you want — coaching, workshops, community calls. No restrictions.',
    },
    {
      title: 'Annual curriculum',
      body: 'A structured annual program with workbooks, designed around where $250K+ firms need to go next. Not generic content — built for your stage.',
    },
    {
      title: 'Your cohort',
      body: "A small group of firm owners running businesses at your level. The room gets better because of who's in it — and not everyone gets in.",
    },
    {
      title: '2-day annual off-site',
      body: 'Once a year, in person, in the Toronto area. Two days with the leadership team and your peers. Included in your membership.',
    },
  ],

  qualifiers: {
    h2: 'Who this is for',
    intro:
      "The Boardroom is not for everyone — and that's by design. This is a small, curated group. Three things are required to apply.",
    items: [
      {
        title: '$250K+ in firm revenue',
        body: "You're running a real business. You have clients, systems, and a team — or you've built something that operates at scale without one. Either way, you're past the early stages.",
      },
      {
        title: 'All Solar Systems completed',
        body: "You've been through Freedom Gateway, Clients, Pricing, and Scale. You've done the work. The Boardroom is what comes after.",
      },
      {
        title: 'Application required',
        body: "Not everyone who applies gets in. We review every application to ensure the room stays at the right level. If it's a fit, you'll hear from us.",
      },
    ],
  },

  applySteps: {
    h2: 'How it works',
    sub: "Three steps. No pressure. If it's the right fit, you'll know — and so will we.",
    steps: [
      {
        title: 'Express interest',
        body: "Fill out the short form below. Tell us about your firm, where you are, and what you're looking to build next.",
      },
      {
        title: 'Conversation',
        body: "If your application looks like a fit, we'll schedule a conversation to learn more about your goals and what you'd bring to the room.",
      },
      {
        title: 'Invitation',
        body: "If it's a mutual fit, you'll receive an invitation to join. Welcome to The Boardroom.",
      },
    ],
  },

  form: {
    anchor: 'apply',
    heading: 'Register your interest',
    sub: "Tell us where to reach you. If it looks like a fit, we'll schedule a conversation.",
    submitLabel: 'Register your interest →',
    successHead: 'Application received.',
    successBody: "If it looks like a fit, we'll reach out to schedule a conversation. Either way, you'll hear from us.",
    source: 'discover-boardroom-application',
  },

  finalH2Plain: 'The table is set.',
  finalH2Accent: 'Is there a seat for you?',
  finalSub:
    "The Boardroom is for a small number of firm owners who've done the work and are ready to build what comes next. If that's you, we'd like to hear from you.",
  finalCtaLabel: 'Register your interest',
};

// Member Pro (Strategy HQ E24/E25/E30): Alumni folds into Member Pro at the
// E27 launch gate. Contents are Member Plus (PLUS_LIST) + PRO_LIST from the
// Lovable pricing source (Bundle Brilliance, src/lib/bkos-pricing.ts; synced to
// commit acfd310, Member Plus expanded 2026-09-27; referral and subcontract board
// added from d336fd2, 2026-10-07, Team Ask mca_b729c23fdf8e). Alumni's
// "all program content", "30–40% off" and "peer cohort" deliberately do NOT
// carry over. Cancellation is by request to the team, never self-serve.
const MEMBER_PRO_CONTENT: TierContent = {
  name: 'Member Pro',
  theme: 'light',
  eyebrow: 'TSB Membership',
  h1Plain: 'Coaching for the firm',
  h1Accent: "you're building",
  heroSub:
    "Member Pro is TSB membership for bookkeepers growing a team and a firm. Live coaching every week, coaches who have scaled firms themselves, and half off every TSB program when you're ready for the next one.",
  heroMeta: '$199/month or $1,990/year · Month to month',
  heroCtaLabel: 'Join Member Pro',

  introH2Plain: "You don't have to",
  introH2Accent: 'figure it out alone.',
  introBody:
    "Growing a bookkeeping firm raises new questions every month: pricing, hiring, leading a team, selling with confidence. Member Pro puts you on a call with people who've answered them, every week, for as long as it's useful.",

  cards: [
    {
      title: 'Coaching every week',
      body: 'The Success Blueprint call and the Team Leadership call every week, plus TSB Live with Michael Palmer and Hot Seat coaching with Lisa Campbell every month.',
    },
    {
      title: 'Coaches who have done it',
      body: "Access to coaches who have scaled bookkeeping firms of their own. Real answers from people who've stood where you're standing.",
    },
    {
      title: 'Half off every program',
      body: "50% off every TSB program — Clients, Freedom Gateway, Scale and more — whenever you're ready to go deeper.",
    },
  ],

  includedH2: "What's included",
  included: [
    {
      title: 'Monthly TSB Live',
      body: 'A live session with Michael Palmer every month, with past podcast guests.',
    },
    {
      title: 'Monthly Hot Seat coaching',
      body: 'Bring a real challenge from your firm and work through it live with Lisa Campbell.',
    },
    {
      title: 'Weekly Success Blueprint call',
      body: 'A coaching call every week to keep your plan moving.',
    },
    {
      title: 'Weekly Team Leadership call',
      body: 'For firm owners leading a team, or getting ready to.',
    },
    {
      title: 'Sales Success Live and Motivational Mindset',
      body: 'Two more live calls every month: one on winning the clients you want, one on the mindset it takes to lead a growing firm.',
    },
    {
      title: 'Coaches who have scaled firms',
      body: "Access to coaches who have built and grown bookkeeping firms of their own.",
    },
    {
      title: 'Two 1:1 Clarity Calls a year',
      body: 'Time with your coach, just you, to step back and set direction.',
    },
    {
      title: '50% off every TSB program',
      body: 'Clients, Freedom Gateway, Scale and more, at half price for as long as you\'re a member.',
    },
    {
      title: 'The full community',
      body: 'Every channel, direct messages, and Ask the Room: put a question to bookkeepers who have been there.',
    },
    {
      title: 'Referral and subcontract board',
      body: 'Trade overflow client work with other members.',
    },
    {
      title: 'Unlimited Ask the Show AI',
      body: 'No limit on how often you use Ask the Show AI.',
    },
    {
      title: 'Partner Webinar Vault',
      body: '60+ partner webinars, organised by topic and stage.',
    },
    {
      title: 'Intensives and workshops included',
      body: 'Free access to Beyond Bookkeeping intensives and quarterly workshops.',
    },
    {
      title: 'Member trainings and VIP Summit access',
      body: 'Member trainings, VIP access to the Summit, and the recordings to go back to.',
    },
    {
      title: 'The Successful Bookkeeper book',
      body: 'A free copy of the book, on us.',
    },
  ],

  forNot: {
    h2: 'Is Member Pro right for you?',
    forTitle: 'Member Pro is for you if…',
    forItems: [
      "You're growing a team, or getting ready to, and want coaching on leading it",
      'You want to be on a live call with people who have scaled a firm, not just read about it',
      'You plan to take TSB programs and want to pay half for them',
      'You want support that keeps going, week in and week out',
    ],
    notTitle: 'Member Pro is not for you if…',
    notItems: [
      "You're looking for a one-time purchase rather than an ongoing membership",
      'You want the monthly coaching calls without the team and program extras. Member Plus may be a better fit',
    ],
  },

  pricing: {
    eyebrow: 'Member Pro',
    price: '$199',
    suffix: '/month',
    terms: 'or $1,990 a year · Month to month',
    features: [
      'Monthly TSB Live with past podcast guests, and Hot Seat coaching with Lisa',
      'Weekly Success Blueprint and Team Leadership calls',
      'Sales Success Live and Motivational Mindset calls',
      'Access to coaches who have scaled firms',
      'Two 1:1 Clarity Calls a year',
      '50% off every TSB program',
      'The full community with Ask the Room, and unlimited Ask the Show AI',
      'Referral and subcontract board: trade overflow client work with other members',
      'Partner Webinar Vault, intensives and quarterly workshops',
      'Member trainings, VIP Summit access and recordings',
      'A free copy of The Successful Bookkeeper book',
    ],
    ctaLabel: 'Join Member Pro',
  },

  form: {
    anchor: 'join',
    heading: 'Ready to join Member Pro?',
    sub: "Leave your name and we'll reach out to get you set up, or book a call and talk it through first.",
    submitLabel: 'Count me in →',
    successHead: "You're on the list.",
    successBody: "We'll reach out shortly to get you set up. Talk soon.",
    source: 'discover-member-pro-interest',
  },

  faqs: [
    {
      q: 'Is there a contract?',
      a: 'No. Member Pro is month to month, and paying annually simply costs less. When you want to cancel, let our team know and we\'ll take care of it. Your access continues to the end of the period you\'ve paid for.',
    },
    {
      q: "What's the difference between Member Plus and Member Pro?",
      a: 'Member Plus gives you monthly TSB Live and Hot Seat coaching, the weekly Success Blueprint call, two 1:1 Clarity Calls a year, the full community with Ask the Room, the referral and subcontract board, unlimited Ask the Show AI, the Partner Webinar Vault, free access to intensives and workshops, and a free copy of the book. Member Pro adds 50% off every TSB program, the Sales Success, Mindset and Team Leadership calls, and access to coaches who have scaled firms.',
    },
    {
      q: 'How does the program discount work?',
      a: 'As a Member Pro member you pay half for any TSB program, including Clients, Freedom Gateway and Scale. Talk with our team when you\'re ready to enroll and they\'ll set it up.',
    },
    {
      q: 'Which currency will I pay in?',
      a: 'The same prices apply in US, Canadian and Australian dollars, and you\'ll see and pay in your own currency. Any applicable sales tax, GST or HST is added at checkout.',
    },
  ],

  finalH2Plain: 'Keep building.',
  finalH2Accent: 'With people who have done it.',
  finalSub:
    'Member Pro is weekly coaching, coaches who have scaled firms, and half off every TSB program, month to month.',
  finalCtaLabel: 'Join Member Pro — $199/month',
};

// Membership tiers with engine-hosted sales pages. ProgramSalesPage routes
// these slugs here instead of the SALES_CONTENT / RSVP paths.
export const MEMBERSHIP_CONTENT: Record<string, TierContent> = {
  alumni: ALUMNI_CONTENT,
  boardroom: BOARDROOM_CONTENT,
  'member-pro': MEMBER_PRO_CONTENT,
};

// ────────────────────────────────────────────────────────────────────────────
// Interest / application form → POST /api/programs/:slug/waitlist
// ────────────────────────────────────────────────────────────────────────────

function InterestForm({ slug, form }: { slug: string; form: TierContent['form'] }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [state, setState] = useState<'form' | 'submitting' | 'success' | 'error'>('form');
  const [errMsg, setErrMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setState('error');
      setErrMsg('Enter your email so we know where to reach you.');
      return;
    }
    setState('submitting');
    setErrMsg('');
    try {
      const res = await fetch(`${API}/api/programs/${encodeURIComponent(slug)}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          name: firstName.trim() || undefined,
          source: form.source,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setState('error');
        setErrMsg(
          res.status === 422
            ? "That email doesn't look deliverable — double-check the spelling?"
            : (body?.error ?? 'Something went wrong. Give it another try?'),
        );
        return;
      }
      setState('success');
    } catch {
      setState('error');
      setErrMsg('Something went wrong. Give it another try?');
    }
  };

  return (
    <div className="tier-form-card" id={form.anchor}>
      {state === 'success' ? (
        <>
          <h3>{form.successHead}</h3>
          <p className="tier-form-sub">{form.successBody}</p>
        </>
      ) : (
        <>
          <h3>{form.heading}</h3>
          <p className="tier-form-sub">{form.sub}</p>
          <form onSubmit={handleSubmit} className="tier-form-row">
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={state === 'submitting'}
            />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state === 'submitting'}
              required
            />
            <button type="submit" disabled={state === 'submitting'}>
              {state === 'submitting' ? 'One moment…' : form.submitLabel}
            </button>
          </form>
          {state === 'error' && errMsg && <p className="tier-form-err">{errMsg}</p>}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FAQ accordion
// ────────────────────────────────────────────────────────────────────────────

function FaqList({ faqs }: { faqs: NonNullable<TierContent['faqs']> }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <div className="tier-faq-list">
      {faqs.map((f, i) => (
        <div key={i} className={`tier-faq-item${openIdx === i ? ' open' : ''}`}>
          <button
            type="button"
            className="tier-faq-q"
            aria-expanded={openIdx === i}
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
          >
            {f.q}
            <span className="tier-faq-toggle" aria-hidden>
              +
            </span>
          </button>
          {openIdx === i && <div className="tier-faq-a">{f.a}</div>}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function MembershipSalesPage({
  slug,
  data,
}: {
  slug: string;
  data: ProgramPublicResponse;
}) {
  const c = MEMBERSHIP_CONTENT[slug];
  useNoIndex(STAGED_SLUGS.has(slug));
  if (!c) return null;

  // When the active cohort gains commerce config (checkout_url + plans on
  // pricing_overrides) the pricing CTA goes straight to the order page.
  // A cohort checkout_url that starts with "/" is an Engine order gate
  // (/checkout/<sku>): the button goes there directly, and each cadence's own
  // plan can carry its own gate path. Anything else keeps the order page.
  const commerceUrl = data.commerce?.checkoutUrl ?? null;
  const isGate = Boolean(commerceUrl?.startsWith('/'));
  const planGate = (cadence: string) =>
    data.commerce?.plans?.find((p) => p.id?.endsWith(`-${cadence}`))?.checkoutUrl ?? null;
  const pricingCtaHref = !commerceUrl
    ? `#${c.form.anchor}`
    : isGate
      ? (planGate('monthly') ?? commerceUrl)
      : `/programs/${slug}/order`;
  const annualGateHref = isGate ? planGate('annual') : null;

  return (
    <div className={`tier-page${c.theme === 'dark' ? ' tier-page--dark' : ''}`}>
      <style>{TIER_CSS}</style>

      {/* ── Top nav ── */}
      <header className="tier-topnav">
        <div className="tier-topnav-inner">
          {data.brand?.logoUrl && (
            <a href="https://www.thesuccessfulbookkeeper.com" className="tier-topnav-logo">
              <img
                src={
                  c.theme === 'dark'
                    ? (data.brand.logoWhiteUrl ?? data.brand.logoUrl)
                    : data.brand.logoUrl
                }
                alt={data.brand.name}
              />
            </a>
          )}
          <a href={`/lets-talk/${slug}`} className="tier-topnav-link">
            Talk with our team
          </a>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="tier-hero">
        <div className="tier-container-hero">
          <div className="tier-eyebrow">★ {c.eyebrow}</div>
          <h1>
            {c.h1Plain}
            <br />
            {c.h1AccentPrefix}
            <span className="tier-accent">{c.h1Accent}</span>
          </h1>
          <p className="tier-hero-sub">{c.heroSub}</p>
          <div className="tier-hero-ctas">
            <a href={pricingCtaHref} className="tier-btn tier-btn-primary">
              {c.heroCtaLabel}
            </a>
            <a href={`/lets-talk/${slug}`} className="tier-btn tier-btn-outline">
              Not sure yet? Talk with our team
            </a>
          </div>
          <p className="tier-hero-meta">{c.heroMeta}</p>
        </div>
      </section>

      {/* ── Intro + cards ── */}
      <section className="tier-section">
        <div className="tier-container">
          <div className="tier-intro">
            <h2>
              {c.introH2Plain}
              <br />
              <span className="tier-accent">{c.introH2Accent}</span>
            </h2>
            <p>{c.introBody}</p>
          </div>
          <div className={`tier-cards tier-cards--${c.cards.length}`}>
            {c.cards.map((card, i) => (
              <div key={i} className="tier-card">
                {card.label && <div className="tier-card-label">{card.label}</div>}
                <h3>{card.title}</h3>
                <p>{card.body}</p>
                {card.bullets && (
                  <ul className="tier-checklist">
                    {card.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who's in the room (Boardroom) ── */}
      {c.people && (
        <section className="tier-section tier-section--alt">
          <div className="tier-container">
            <h2 className="tier-h2-center">{c.people.h2}</h2>
            <p className="tier-section-sub">{c.people.sub}</p>
            <div className="tier-people-grid">
              {c.people.items.map((p, i) => (
                <div key={i} className="tier-person">
                  <div className="tier-person-avatar">{p.initials}</div>
                  <div className="tier-person-name">{p.name}</div>
                  <div className="tier-person-role">{p.role}</div>
                  <p className="tier-person-bio">{p.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── What's included ── */}
      <section className={`tier-section${c.people ? '' : ' tier-section--alt'}`}>
        <div className="tier-container">
          <h2 className="tier-h2-center">{c.includedH2}</h2>
          <div className="tier-included-grid">
            {c.included.map((item, i) => (
              <div key={i} className="tier-included-item">
                <span className="tier-included-mark" aria-hidden>
                  ✓
                </span>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── For / not for (Alumni) ── */}
      {c.forNot && (
        <section className="tier-section">
          <div className="tier-container">
            <h2 className="tier-h2-center">{c.forNot.h2}</h2>
            <div className="tier-who-cols">
              <div className="tier-who-col tier-who-col--for">
                <h3>{c.forNot.forTitle}</h3>
                <ul>
                  {c.forNot.forItems.map((item, i) => (
                    <li key={i} className="tier-li-yes">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="tier-who-col tier-who-col--not">
                <h3>{c.forNot.notTitle}</h3>
                <ul>
                  {c.forNot.notItems.map((item, i) => (
                    <li key={i} className="tier-li-no">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Qualifiers (Boardroom) ── */}
      {c.qualifiers && (
        <section className="tier-section">
          <div className="tier-container">
            <h2 className="tier-h2-center">{c.qualifiers.h2}</h2>
            <p className="tier-section-sub">{c.qualifiers.intro}</p>
            <div className="tier-qualifiers">
              {c.qualifiers.items.map((q, i) => (
                <div key={i} className="tier-qualifier">
                  <div className="tier-qualifier-num">{i + 1}</div>
                  <div>
                    <h4>{q.title}</h4>
                    <p>{q.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Testimonials (Alumni) ── */}
      {c.testimonials && (
        <section className="tier-section tier-section--alt tier-section--tight">
          <div className="tier-container">
            <h2 className="tier-h2-center">{c.testimonials.h2}</h2>
            <div className="tier-proof-grid">
              {c.testimonials.items.map((t, i) => (
                <div key={i} className="tier-proof-card">
                  <blockquote>{t.quote}</blockquote>
                  <div className="tier-proof-attr">
                    <div className="tier-proof-avatar">{t.initials}</div>
                    <div>
                      {t.name && <div className="tier-proof-name">{t.name}</div>}
                      <div className="tier-proof-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Pricing (Alumni) ── */}
      {c.pricing && (
        <section className="tier-section">
          <div className="tier-container">
            <div className="tier-pricing-card">
              <div className="tier-pricing-eyebrow">{c.pricing.eyebrow}</div>
              <div className="tier-pricing-price">
                {c.pricing.price}
                <span>{c.pricing.suffix}</span>
              </div>
              <div className="tier-pricing-terms">{c.pricing.terms}</div>
              <div className="tier-pricing-divider" />
              <ul className="tier-checklist tier-checklist--onDark">
                {c.pricing.features.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
              <a href={pricingCtaHref} className="tier-btn tier-btn-primary tier-btn-block">
                {c.pricing.ctaLabel}
              </a>
              {annualGateHref && (
                <a href={annualGateHref} className="tier-pricing-secondary">
                  Prefer to pay yearly? Join {c.name} for $1,990 a year →
                </a>
              )}
              <a href={`/lets-talk/${slug}`} className="tier-pricing-secondary">
                Talk with our team about {c.name} →
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ── Application steps (Boardroom) ── */}
      {c.applySteps && (
        <section className="tier-section tier-section--alt">
          <div className="tier-container">
            <h2 className="tier-h2-center">{c.applySteps.h2}</h2>
            <p className="tier-section-sub">{c.applySteps.sub}</p>
            <div className="tier-steps-row">
              {c.applySteps.steps.map((s, i) => (
                <div key={i} className="tier-step-card">
                  <div className="tier-step-num">{i + 1}</div>
                  <h4>{s.title}</h4>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Interest / application form ── */}
      <section className={`tier-section${c.applySteps ? ' tier-section--alt tier-section--tight-top' : ''}`}>
        <div className="tier-container">
          <InterestForm slug={slug} form={c.form} />
        </div>
      </section>

      {/* ── FAQ (Alumni) ── */}
      {c.faqs && (
        <section className="tier-section tier-section--alt">
          <div className="tier-container">
            <h2 className="tier-h2-center">Common questions</h2>
            <FaqList faqs={c.faqs} />
          </div>
        </section>
      )}

      {/* ── Final CTA ── */}
      <section className="tier-final">
        <div className="tier-container-hero">
          <h2>
            {c.finalH2Plain}
            <br />
            <span className="tier-accent">{c.finalH2Accent}</span>
          </h2>
          <p>{c.finalSub}</p>
          <div className="tier-hero-ctas">
            <a href={pricingCtaHref} className="tier-btn tier-btn-primary">
              {c.finalCtaLabel}
            </a>
            <a href={`/lets-talk/${slug}`} className="tier-btn tier-btn-ghost">
              Talk with our team
            </a>
          </div>
          {commerceUrl && (
            <a href={`#${c.form.anchor}`} className="tier-pricing-secondary">
              Not ready yet? Register your interest instead
            </a>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="tier-footer">
        <a href="https://www.thesuccessfulbookkeeper.com">The Successful Bookkeeper</a>
        <span className="tier-footer-sep">·</span>
        <span>Built for Freedom.</span>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Styles — driven by the --prog-* / --font-* tokens the parent applies from
// programs_branding (Alumni: navy + gold; Boardroom: navy + teal).
// ────────────────────────────────────────────────────────────────────────────

const TIER_CSS = `
.tier-page {
  --t-accent: var(--prog-primary, #F5C300);
  --t-navy: var(--prog-text-dark, #0A0A2E);
  --t-deep: var(--prog-deep, #1A1A3E);
  --t-canvas: #FFFFFF;
  --t-canvas-alt: #FAFAF7;
  --t-ink: #0A0A2E;
  --t-ink-dim: #4A4A6E;
  --t-ink-quiet: #8A8AA8;
  --t-hairline: rgba(10, 10, 46, 0.08);
  --t-card: #FFFFFF;
  font-family: var(--font-body, 'Poppins', -apple-system, sans-serif);
  background: var(--t-canvas);
  color: var(--t-ink);
  font-size: 17px;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}
.tier-page--dark {
  --t-canvas: #0A0A2E;
  --t-canvas-alt: #11113B;
  --t-ink: #FFFFFF;
  --t-ink-dim: rgba(255, 255, 255, 0.78);
  --t-ink-quiet: rgba(255, 255, 255, 0.55);
  --t-hairline: rgba(255, 255, 255, 0.1);
  --t-card: #15154A;
}
.tier-page h1, .tier-page h2, .tier-page h3, .tier-page h4 {
  font-family: var(--font-heading, 'Poppins', -apple-system, sans-serif);
  margin: 0;
}
.tier-page p, .tier-page ul { margin: 0; }
.tier-page ul { list-style: none; padding: 0; }
.tier-page a { text-decoration: none; color: inherit; }
.tier-page h1 { font-size: clamp(40px, 5.6vw, 72px); font-weight: 800; letter-spacing: -0.02em; line-height: 1.08; }
.tier-page h2 { font-size: clamp(30px, 4.2vw, 48px); font-weight: 800; letter-spacing: -0.02em; line-height: 1.12; }
.tier-accent { color: var(--t-accent); }

.tier-container { max-width: 1080px; margin: 0 auto; padding: 0 24px; }
.tier-container-hero { max-width: 860px; margin: 0 auto; padding: 0 24px; }
.tier-section { padding: 96px 0; background: var(--t-canvas); }
.tier-section--alt { background: var(--t-canvas-alt); }
.tier-section--tight { padding: 72px 0; }
.tier-section--tight-top { padding-top: 0; }

.tier-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-weight: 600; font-size: 16px; padding: 16px 32px; border-radius: 8px;
  border: none; cursor: pointer; line-height: 1;
  transition: transform 0.2s ease, box-shadow 0.25s ease, opacity 0.2s ease;
}
.tier-btn:hover { transform: translateY(-1px); }
.tier-btn-primary { background: var(--t-accent); color: #0A0A2E; }
.tier-btn-primary:hover { box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18); }
.tier-btn-outline { background: transparent; color: var(--t-ink-dim); border: 1.5px solid var(--t-hairline); }
.tier-btn-outline:hover { border-color: var(--t-accent); color: var(--t-ink); }
.tier-btn-ghost { background: transparent; color: rgba(255,255,255,0.85); border: 1.5px solid rgba(255,255,255,0.25); }
.tier-btn-ghost:hover { border-color: rgba(255,255,255,0.6); color: #fff; }
.tier-btn-block { width: 100%; padding: 18px 32px; font-size: 17px; }

.tier-topnav { background: var(--t-canvas); border-bottom: 1px solid var(--t-hairline); padding: 12px 24px; }
.tier-topnav-inner { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.tier-topnav-logo img { height: 36px; width: auto; display: block; }
.tier-topnav-link { font-size: 13px; font-weight: 600; color: var(--t-accent); border-bottom: 2px solid var(--t-accent); padding-bottom: 1px; }

.tier-hero { padding: 110px 0 92px; text-align: center; background: var(--t-canvas-alt); position: relative; overflow: hidden; }
.tier-hero::before {
  content: ''; position: absolute; top: -30%; right: -10%; width: 600px; height: 600px;
  background: radial-gradient(circle, color-mix(in srgb, var(--t-accent) 9%, transparent) 0%, transparent 70%);
  pointer-events: none;
}
.tier-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--t-accent); margin-bottom: 24px; }
.tier-hero h1 { margin-bottom: 24px; position: relative; }
.tier-hero-sub { font-size: 19px; color: var(--t-ink-dim); max-width: 640px; margin: 0 auto 40px; position: relative; }
.tier-hero-ctas { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; position: relative; }
.tier-hero-meta { font-size: 14px; color: var(--t-ink-quiet); font-weight: 500; position: relative; }

.tier-intro { text-align: center; margin-bottom: 64px; }
.tier-intro h2 { margin-bottom: 20px; }
.tier-intro p { font-size: 18px; color: var(--t-ink-dim); max-width: 660px; margin: 0 auto; }

.tier-cards { display: grid; gap: 28px; }
.tier-cards--2 { grid-template-columns: repeat(2, 1fr); max-width: 880px; margin: 0 auto; }
.tier-cards--3 { grid-template-columns: repeat(3, 1fr); }
.tier-card { background: var(--t-card); border-radius: 22px; padding: 40px 32px; border: 1px solid var(--t-hairline); }
.tier-cards--3 .tier-card { text-align: center; background: var(--t-canvas-alt); }
.tier-card-label { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--t-accent); margin-bottom: 14px; }
.tier-card h3 { font-size: 22px; font-weight: 700; margin-bottom: 10px; }
.tier-card p { font-size: 15px; color: var(--t-ink-dim); }
.tier-card .tier-checklist { margin-top: 20px; }

.tier-checklist { display: flex; flex-direction: column; gap: 12px; text-align: left; }
.tier-checklist li { font-size: 15px; color: var(--t-ink-dim); line-height: 1.5; padding-left: 28px; position: relative; }
.tier-checklist li::before { content: '✓'; position: absolute; left: 0; color: var(--t-accent); font-weight: 700; }
.tier-checklist--onDark li { color: rgba(255,255,255,0.82); }

.tier-h2-center { text-align: center; margin-bottom: 20px; }
.tier-section-sub { text-align: center; font-size: 17px; color: var(--t-ink-dim); max-width: 620px; margin: 0 auto 56px; }

.tier-people-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 56px; }
.tier-person { background: var(--t-card); border-radius: 18px; padding: 32px 28px; border: 1px solid var(--t-hairline); }
.tier-person-avatar { width: 52px; height: 52px; border-radius: 50%; background: color-mix(in srgb, var(--t-accent) 14%, transparent); color: var(--t-accent); display: flex; align-items: center; justify-content: center; font-size: 17px; font-weight: 700; margin-bottom: 16px; }
.tier-person-name { font-size: 17px; font-weight: 700; }
.tier-person-role { font-size: 13px; color: var(--t-accent); font-weight: 600; margin-bottom: 10px; }
.tier-person-bio { font-size: 14px; color: var(--t-ink-dim); line-height: 1.6; }

.tier-included-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-top: 56px; }
.tier-included-item { display: flex; gap: 18px; align-items: flex-start; background: var(--t-card); border-radius: 18px; padding: 28px 24px; border: 1px solid var(--t-hairline); }
.tier-included-mark { flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px; background: color-mix(in srgb, var(--t-accent) 12%, transparent); color: var(--t-accent); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 18px; }
.tier-included-item h4 { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
.tier-included-item p { font-size: 15px; color: var(--t-ink-dim); line-height: 1.55; }

.tier-who-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 56px; }
.tier-who-col { border-radius: 22px; padding: 40px 36px; }
.tier-who-col--for { background: var(--t-navy); color: #fff; }
.tier-who-col--not { background: var(--t-canvas-alt); border: 1px solid var(--t-hairline); }
.tier-who-col h3 { font-size: 22px; margin-bottom: 24px; }
.tier-who-col--for h3 { color: var(--t-accent); }
.tier-who-col--not h3 { color: var(--t-ink-dim); }
.tier-who-col ul { display: flex; flex-direction: column; gap: 16px; }
.tier-who-col li { font-size: 15px; line-height: 1.55; padding-left: 30px; position: relative; }
.tier-who-col--for li { color: rgba(255,255,255,0.88); }
.tier-who-col--not li { color: var(--t-ink-dim); }
.tier-li-yes::before { content: '✓'; position: absolute; left: 0; color: var(--t-accent); font-weight: 700; }
.tier-li-no::before { content: '✕'; position: absolute; left: 0; color: var(--t-ink-quiet); font-weight: 600; }

.tier-qualifiers { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px; }
.tier-qualifier { display: flex; gap: 20px; align-items: flex-start; background: var(--t-card); border-radius: 18px; padding: 28px 26px; border: 1px solid var(--t-hairline); }
.tier-qualifier-num { flex-shrink: 0; width: 44px; height: 44px; border-radius: 50%; background: color-mix(in srgb, var(--t-accent) 14%, transparent); color: var(--t-accent); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px; }
.tier-qualifier h4 { font-size: 17px; font-weight: 700; margin-bottom: 6px; }
.tier-qualifier p { font-size: 15px; color: var(--t-ink-dim); line-height: 1.6; }

.tier-proof-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 48px; }
.tier-proof-card { background: var(--t-card); border-radius: 22px; padding: 40px 36px; border: 1px solid var(--t-hairline); }
.tier-proof-card blockquote { font-size: 17px; font-style: italic; color: var(--t-ink-dim); line-height: 1.65; margin: 0 0 24px; }
.tier-proof-attr { display: flex; align-items: center; gap: 14px; }
.tier-proof-avatar { width: 44px; height: 44px; border-radius: 50%; background: color-mix(in srgb, var(--t-accent) 12%, transparent); color: var(--t-accent); display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; }
.tier-proof-name { font-size: 15px; font-weight: 600; }
.tier-proof-role { font-size: 13px; color: var(--t-ink-quiet); }

.tier-pricing-card { max-width: 560px; margin: 0 auto; background: var(--t-navy); border-radius: 22px; padding: 56px 48px; text-align: center; color: #fff; position: relative; overflow: hidden; }
.tier-pricing-card::before { content: ''; position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 80px; height: 4px; border-radius: 0 0 4px 4px; background: var(--t-accent); }
.tier-pricing-eyebrow { font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--t-accent); margin-bottom: 16px; }
.tier-pricing-price { font-size: 56px; font-weight: 800; letter-spacing: -0.03em; margin-bottom: 4px; line-height: 1; }
.tier-pricing-price span { font-size: 22px; font-weight: 500; color: rgba(255,255,255,0.6); }
.tier-pricing-terms { font-size: 14px; color: rgba(255,255,255,0.55); margin-bottom: 32px; }
.tier-pricing-divider { height: 1px; background: rgba(255,255,255,0.1); margin: 0 0 28px; }
.tier-pricing-card .tier-checklist { margin-bottom: 36px; }
.tier-pricing-secondary { display: inline-block; margin-top: 14px; font-size: 14px; color: rgba(255,255,255,0.55); }
.tier-pricing-secondary:hover { color: rgba(255,255,255,0.8); }

.tier-steps-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 56px; }
.tier-step-card { background: var(--t-card); border-radius: 18px; padding: 32px 28px; border: 1px solid var(--t-hairline); text-align: center; }
.tier-step-num { width: 44px; height: 44px; border-radius: 50%; background: var(--t-accent); color: #0A0A2E; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px; margin: 0 auto 18px; }
.tier-step-card h4 { font-size: 17px; font-weight: 700; margin-bottom: 8px; }
.tier-step-card p { font-size: 14px; color: var(--t-ink-dim); line-height: 1.6; }

.tier-form-card { max-width: 560px; margin: 0 auto; background: var(--t-card); border: 1px solid var(--t-hairline); border-radius: 22px; padding: 44px 40px; text-align: center; box-shadow: 0 18px 48px rgba(0,0,0,0.08); scroll-margin-top: 24px; }
.tier-form-card h3 { font-size: 24px; font-weight: 700; margin-bottom: 10px; }
.tier-form-sub { font-size: 15px; color: var(--t-ink-dim); margin-bottom: 24px; }
.tier-form-row { display: flex; flex-direction: column; gap: 12px; }
.tier-form-row input { font-family: inherit; font-size: 16px; padding: 13px 16px; border-radius: 8px; border: 1px solid var(--t-hairline); background: var(--t-canvas); color: var(--t-ink); outline: none; width: 100%; box-sizing: border-box; }
.tier-form-row input:focus { border-color: var(--t-accent); }
.tier-form-row button { font-family: inherit; font-size: 16px; font-weight: 700; color: #0A0A2E; background: var(--t-accent); border: none; border-radius: 8px; padding: 14px 20px; cursor: pointer; }
.tier-form-err { font-size: 14px; color: #E06060; margin-top: 14px; }

.tier-faq-list { max-width: 720px; margin: 48px auto 0; display: flex; flex-direction: column; gap: 12px; }
.tier-faq-item { background: var(--t-card); border-radius: 14px; border: 1px solid var(--t-hairline); overflow: hidden; }
.tier-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 24px; font-family: inherit; font-size: 16px; font-weight: 600; text-align: left; cursor: pointer; background: transparent; border: none; color: var(--t-ink); }
.tier-faq-toggle { color: var(--t-ink-quiet); font-size: 20px; font-weight: 400; transition: transform 0.25s ease; }
.tier-faq-item.open .tier-faq-toggle { transform: rotate(45deg); }
.tier-faq-a { padding: 0 24px 22px; font-size: 15px; color: var(--t-ink-dim); line-height: 1.65; }

.tier-final { background: var(--t-navy); padding: 100px 0; text-align: center; position: relative; overflow: hidden; }
.tier-final::before { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 700px; height: 500px; background: radial-gradient(ellipse, color-mix(in srgb, var(--t-accent) 7%, transparent) 0%, transparent 70%); pointer-events: none; }
.tier-final h2 { color: #fff; margin-bottom: 16px; position: relative; }
.tier-final p { font-size: 18px; color: rgba(255,255,255,0.65); max-width: 540px; margin: 0 auto 40px; position: relative; }

.tier-footer { background: var(--prog-text-dark, #0A0A2E); color: rgba(255,255,255,0.7); font-size: 13px; text-align: center; padding: 18px 24px; border-top: 1px solid rgba(255,255,255,0.08); }
.tier-footer a { color: rgba(255,255,255,0.85); font-weight: 600; }
.tier-footer-sep { margin: 0 8px; opacity: 0.5; }

@media (max-width: 760px) {
  .tier-hero { padding: 80px 0 64px; }
  .tier-section { padding: 64px 0; }
  .tier-cards--2, .tier-cards--3 { grid-template-columns: 1fr; gap: 16px; }
  .tier-people-grid { grid-template-columns: 1fr; }
  .tier-included-grid { grid-template-columns: 1fr; }
  .tier-who-cols { grid-template-columns: 1fr; gap: 20px; }
  .tier-proof-grid { grid-template-columns: 1fr; }
  .tier-steps-row { grid-template-columns: 1fr; }
  .tier-hero-ctas { flex-direction: column; }
  .tier-hero-ctas .tier-btn { width: 100%; }
  .tier-pricing-card { padding: 40px 28px; }
  .tier-form-card { padding: 36px 24px; }
  .tier-final { padding: 72px 0; }
}
`;
